<template>
  <div class="cfg" lang="es-MX">
    <button type="button" class="muted" @click="$emit('volver')">← Cursos</button>
    <header class="head" v-if="nivel">
      <p class="eyebrow">{{ marca }}</p>
      <h1>{{ nivel.nombre }}</h1>
      <p class="lead">Configura cada sección del nivel. Las lecciones y sus videos se guardan en Video.</p>
    </header>

    <div class="panel-secciones">
      <button
        v-for="s in secciones"
        :key="s.id"
        type="button"
        :class="{ on: seccion === s.id }"
        @click="seccion = s.id"
      >
        {{ s.titulo }}
      </button>
    </div>

    <v-alert v-if="error" type="error" class="mb-4" dismissible @input="error = ''">{{ error }}</v-alert>
    <v-alert v-if="ok" type="success" dense class="mb-4" dismissible @input="ok = ''">{{ ok }}</v-alert>

    <section class="soft-card nivel-box">
      <h2 class="box-title">Datos del nivel</h2>
      <div class="nivel-info">
        <div class="info-item">
          <span class="info-label">Nombre</span>
          <p class="info-value">{{ nivel.nombre }}</p>
        </div>
        <div class="info-item">
          <span class="info-label">Subtítulo</span>
          <p class="info-value">{{ subtitulo }}</p>
        </div>
        <div class="info-item">
          <span class="info-label">Estado</span>
          <p class="info-value">{{ estadoLabel }}</p>
        </div>
        <div class="info-item full">
          <span class="info-label">Descripción</span>
          <p class="info-value">{{ nivel.descripcion || "Sin descripción" }}</p>
        </div>
      </div>
      <p class="nivel-hint">Para editar estos datos, usa Editar en la lista de cursos.</p>
    </section>

    <template v-if="seccion === 'video'">
    <div class="section-head">
      <h2 class="section-h">Lecciones</h2>
      <button class="btn refresh" type="button" :disabled="actualizando" @click="actualizarVista">
        {{ actualizando ? "Actualizando…" : "Actualizar vista" }}
      </button>
    </div>

    <article v-for="l in lecciones" :key="l.id" :ref="'leccion-' + l.id" class="lesson-card" :class="{ open: abierta === l.id }">
      <button type="button" class="lesson-summary" @click="toggleLeccion(l)">
        <div class="summary-main">
          <h3>{{ l.titulo || "Lección" }}</h3>
          <p class="summary-desc">{{ l.descripcion || "Sin descripción" }}</p>
        </div>
        <div class="summary-right">
          <span class="badge" :class="l.video_id ? 'ok' : 'off'">
            {{ l.video_id ? "Con video" : "Sin video" }}
          </span>
          <span class="chevron">{{ abierta === l.id ? "▾" : "▸" }}</span>
        </div>
      </button>

      <div v-if="abierta === l.id" class="lesson-body">
        <div class="grid-2">
          <div class="fields">
            <v-text-field v-model="l.titulo" label="Título" outlined hide-details class="mb-3" lang="es-MX" />
            <v-textarea v-model="l.descripcion" label="Descripción del video" outlined rows="3" hide-details class="mb-3" lang="es-MX" />
            <v-text-field v-model.number="l.orden" label="Orden" type="number" outlined hide-details class="mb-3" />
            <p class="duracion-auto">
              Duración:
              <strong>{{ formatearDuracion(l.duracion_segundos) }}</strong>
              <span class="hint"> (automática al subir el video)</span>
            </p>
            <button class="btn ghost" type="button" @click="pedirGuardarLeccion(l)">Guardar datos</button>
            <button class="btn-del-lesson" type="button" @click="pedirEliminarLeccion(l)">Eliminar lección</button>
          </div>

          <div class="video-panel">
            <p class="panel-label">Video de la clase</p>
            <p v-if="l.video_id" class="file-ok">Archivo cargado</p>
            <p v-else class="file-miss">Ningún archivo cargado</p>
            <input
              :ref="'file-' + l.id"
              class="hidden-file"
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/*"
              @change="elegirArchivo($event, l)"
            />
            <p v-if="archivos[l.id]" class="picked">
              {{ archivos[l.id].name }} · {{ peso(archivos[l.id].size) }}
            </p>
            <div class="actions">
              <button class="btn ghost" type="button" @click="abrirFile(l.id)">Elegir archivo</button>
              <button class="btn" type="button" :disabled="subiendo === l.id || !archivos[l.id]" @click="subir(l)">
                {{ subiendo === l.id ? "Subiendo…" : "Subir video" }}
              </button>
            </div>
            <div v-if="subiendo === l.id" class="upload-bar">
              <v-progress-linear :value="progreso[l.id] || 0" height="10" rounded color="teal" />
              <p class="upload-pct">{{ progreso[l.id] || 0 }}%</p>
            </div>
            <video
              v-if="l.video_id"
              class="preview"
              :src="urlVideo(l)"
              preload="metadata"
              controls
              controlslist="nodownload noremoteplayback"
              disablePictureInPicture
              @contextmenu.prevent
            />
          </div>
        </div>
        <div class="portada-box">
          <p class="panel-label">Portada</p>
          <p class="nivel-hint">Esta imagen queda de fondo en la tarjeta de Video de la app.</p>
          <input
            :ref="'portada-' + l.id"
            class="hidden-file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            @change="elegirPortada($event, l)"
          />
          <div class="actions">
            <button class="btn ghost" type="button" @click="abrirPortada(l.id)">Elegir portada</button>
            <button class="btn" type="button" :disabled="!tienePortada(l)" @click="abrirAjuste(l)">Ajustar portada</button>
          </div>
          <p class="preview-label">Así se verá en la app</p>
          <div :ref="'vista-' + l.id" class="app-preview" :class="{ 'con-foto': tienePortada(l) }">
            <img v-if="tienePortada(l)" class="app-preview-foto" alt="" :src="portadasLocal[l.id] || urlPortada(l)" :style="estiloPreview(l)" @load="medirPreview(l, $event)" />
            <span class="preview-kicker">Video</span>
            <strong>{{ l.titulo || "Video" }}</strong>
            <p>{{ l.descripcion || "Sin descripción" }}</p>
            <span class="preview-cta">Empezar</span>
          </div>
        </div>
      </div>
    </article>

    <div v-if="!lecciones.length" class="lesson-card add">
      <h3>Nueva lección</h3>
      <div class="row-nueva">
        <v-text-field v-model="nueva.titulo" label="Título" outlined dense hide-details lang="es-MX" />
        <v-text-field v-model.number="nueva.orden" label="Orden" type="number" outlined dense hide-details />
      </div>
      <v-textarea v-model="nueva.descripcion" label="Descripción del video" outlined dense rows="3" hide-details class="mb-3" lang="es-MX" />
      <div class="nueva-video">
        <p class="panel-label">Video (opcional)</p>
        <input ref="fileNueva" class="hidden-file" type="file" accept="video/mp4,video/webm,video/quicktime,video/*" @change="elegirArchivoNueva" />
        <div class="actions">
          <button class="btn ghost" type="button" @click="abrirFileNueva">Elegir video</button>
          <span v-if="archivoNueva" class="picked">{{ archivoNueva.name }} · {{ peso(archivoNueva.size) }}</span>
        </div>
        <div v-if="creando" class="upload-bar">
          <v-progress-linear :value="progresoNueva || 0" height="10" rounded color="teal" />
          <p class="upload-pct">{{ progresoNueva || 0 }}%</p>
        </div>
      </div>
      <p v-if="faltantesNueva.length" class="faltantes">Falta por agregar: {{ faltantesNueva.join(" y ") }}.</p>
      <button class="btn" type="button" :disabled="creando" @click="crearLeccion">
        {{ creando ? "Agregando…" : "Agregar lección" }}
      </button>
    </div>
    </template>

    <section v-else-if="esGramatica" class="soft-card vacio-seccion">
      <h2 class="box-title">Ejercicios de gramática</h2>
      <p class="nivel-hint">La explicación y los enunciados son del módulo. En la app cada enunciado sale a veces para elegir la palabra y a veces para arrastrarla. No hace falta separarlos.</p>

      <label class="campo-label">Explicación del módulo</label>
      <textarea v-model="moduloGramatica.explicacion" class="campo-area" rows="4" placeholder="Esta explicación es la descripción que verá el alumno."></textarea>

      <button class="btn ghost despliegue" type="button" @click="mensajesAbiertos = !mensajesAbiertos">
        {{ mensajesAbiertos ? "Ocultar mensaje de calificación" : "Editar mensaje de calificación" }}
      </button>
      <div v-if="moduloGramatica.mensajes" v-show="mensajesAbiertos" class="mensajes-box">
        <p class="nivel-hint">Estos textos salen al terminar los ejercicios. Se publican con Liberar a la app.</p>
        <div v-for="grupo in gruposMensaje" :key="grupo.id" class="mensaje-item">
          <p class="campo-label">{{ grupo.etiqueta }}</p>
          <input v-model="moduloGramatica.mensajes[grupo.id].titulo" class="campo-texto" type="text" placeholder="Título" />
          <textarea v-model="moduloGramatica.mensajes[grupo.id].mensaje" class="campo-area" rows="3" placeholder="Mensaje"></textarea>
        </div>
      </div>

      <label class="campo-label">Ejercicios que se muestran en la app</label>
      <input v-model.number="moduloGramatica.max_ejercicios" class="campo-num" type="number" min="1" step="1" />
      <p class="nivel-hint">De la lista de abajo, la app enseña solo esta cantidad, en orden distinto cada vez.</p>

      <h3 class="box-title">Enunciados</h3>
      <div class="fila-enunciado">
        <input v-model="enunciadoNuevo" class="campo-texto" type="text" placeholder="Escribe un enunciado y agrégalo a la lista" @keyup.enter="agregarEnunciado" />
        <button class="btn" type="button" @click="agregarEnunciado">Agregar</button>
      </div>

      <article
        v-for="(ej, i) in moduloGramatica.ejercicios"
        :key="'ej-' + i"
        :ref="'enunciado-' + i"
        class="lesson-card enunciado-card"
        :class="{ open: enunciadoAbierto === i }"
      >
        <button type="button" class="lesson-summary" @click="alternarEnunciado(i)">
          <div class="summary-main">
            <h3>Enunciado {{ i + 1 }}</h3>
            <p class="summary-desc">{{ enunciadoConBlanco(ej) || "Sin texto" }}</p>
          </div>
          <span class="chevron">{{ enunciadoAbierto === i ? "▾" : "▸" }}</span>
        </button>
        <div v-if="enunciadoAbierto === i" class="lesson-body">
          <input v-model="ej.enunciado" class="campo-texto" type="text" @input="alEditarEnunciado(ej)" />
          <p class="nivel-hint">Elige la palabra que se queda en blanco.</p>
          <div class="palabras">
            <button
              v-for="(p, pi) in palabrasDe(ej.enunciado)"
              :key="'p-' + i + '-' + pi"
              type="button"
              class="palabra-chip"
              :class="{ on: ej.indice === pi }"
              @click="elegirPalabra(ej, pi)"
            >
              {{ p }}
            </button>
          </div>
          <p v-if="ej.indice != null" class="enunciado-vista">{{ enunciadoConBlanco(ej) }}</p>
          <p class="nivel-hint">Palabras que verá el alumno. Siempre son 3. En la app salen revueltas.</p>
          <div class="tres-palabras">
            <input v-model="ej.opciones[0]" class="campo-texto" type="text" placeholder="Correcta" />
            <input v-model="ej.opciones[1]" class="campo-texto" type="text" placeholder="Opción 2" />
            <input v-model="ej.opciones[2]" class="campo-texto" type="text" placeholder="Opción 3" />
          </div>
          <button class="btn ghost btn-quitar" type="button" @click="quitarEnunciado(i)">Quitar</button>
        </div>
      </article>

      <div v-if="gramaticaSucia || gramaticaListaLiberar" class="confirm-actions">
        <button v-if="gramaticaSucia" class="btn" type="button" :disabled="guardandoGramatica" @click="guardarGramatica(false)">
          {{ guardandoGramatica ? "Guardando…" : "Guardar gramática" }}
        </button>
        <button v-if="gramaticaListaLiberar && !gramaticaSucia" class="btn" type="button" :disabled="guardandoGramatica" @click="guardarGramatica(true)">
          Liberar a la app
        </button>
      </div>
    </section>

    <section v-else-if="esHabla" class="soft-card vacio-seccion">
      <h2 class="box-title">Habla</h2>
      <p class="nivel-hint">Sube o graba el audio que el alumno va a escuchar. Debajo escribe la respuesta correcta, tal como debe pronunciarse. En la app graba su voz y esa toma se califica sin modificar el audio.</p>
      <input ref="fileHabla" class="hidden-file" type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/mp4,audio/webm,audio/ogg,audio/*" @change="elegirAudioHabla" />
      <label class="campo-label">Audios que se muestran en la app</label>
      <input v-model.number="habla.max_audios" class="campo-num" type="number" min="1" step="1" />
      <div class="fila-enunciado">
        <button class="btn btn-agregar-audio" type="button" @click="agregarHabla">Agregar audio</button>
      </div>
      <article
        v-for="(au, i) in habla.audios"
        :key="'hb-' + i"
        :ref="'habla-' + i"
        class="lesson-card enunciado-card"
        :class="{ open: hablaAbierto === i }"
      >
        <button type="button" class="lesson-summary" @click="alternarHabla(i)">
          <div class="summary-main">
            <h3>Audio {{ i + 1 }}</h3>
            <p class="summary-desc">{{ au.transcripcion || "Sin respuesta correcta" }}</p>
          </div>
          <span class="chevron">{{ hablaAbierto === i ? "▾" : "▸" }}</span>
        </button>
        <div v-if="hablaAbierto === i" class="lesson-body">
          <div v-if="grabando && audioGrabando === i && seccion === 'habla'" class="grabando-box">
            <span class="grabando-punto"></span>
            <div>
              <strong>Grabando</strong>
              <p>{{ textoGrabacion() }}</p>
            </div>
            <button class="btn grabando-stop" type="button" @click="detenerGrabacion">Detener</button>
          </div>
          <div v-else class="actions">
            <button class="btn ghost" type="button" @click="abrirAudioHabla(i)">Elegir archivo</button>
            <button class="btn ghost" type="button" @click="grabarHabla(i)">Grabar</button>
            <span v-if="au.audioNombre" class="picked">{{ au.audioNombre }}</span>
          </div>
          <audio v-if="!(grabando && audioGrabando === i) && previewHabla(au)" class="audio-prev" controls :src="previewHabla(au)"></audio>
          <label class="campo-label">Respuesta correcta</label>
          <textarea v-model="au.transcripcion" class="campo-area" rows="2" placeholder="Lo que el alumno debe decir."></textarea>
          <button class="btn ghost btn-quitar" type="button" @click="quitarHabla(i)">Quitar</button>
        </div>
      </article>
      <div v-if="hablaSucio || hablaListaLiberar" class="confirm-actions">
        <button v-if="hablaSucio" class="btn" type="button" :disabled="guardandoHabla" @click="guardarHabla(false)">
          {{ guardandoHabla ? "Guardando…" : "Guardar" }}
        </button>
        <button v-if="hablaListaLiberar && !hablaSucio" class="btn" type="button" :disabled="guardandoHabla" @click="guardarHabla(true)">
          Liberar a la app
        </button>
      </div>
    </section>

    <section v-else-if="esEscribe" class="soft-card vacio-seccion">
      <h2 class="box-title">Escribe</h2>
      <p class="nivel-hint">Cada audio lleva la transcripción. En la app el alumno la escribe y tiene que coincidir exactamente. Si falla dos veces, un foquito junto al audio sugiere cómo va la parte que no coincidió, sin escribirla completa.</p>
      <input ref="fileEscribe" class="hidden-file" type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/mp4,audio/webm,audio/ogg,audio/*" @change="elegirAudioEscribe" />

      <label class="campo-label">Audios que se muestran en la app</label>
      <input v-model.number="escribe.max_audios" class="campo-num" type="number" min="1" step="1" />
      <p class="nivel-hint">De la lista de abajo, la app enseña solo esta cantidad, en orden distinto cada vez.</p>

      <div class="fila-enunciado">
        <button class="btn btn-agregar-audio" type="button" @click="agregarAudio">Agregar audio</button>
      </div>

      <article
        v-for="(au, i) in escribe.audios"
        :key="'au-' + i"
        :ref="'audio-' + i"
        class="lesson-card enunciado-card"
        :class="{ open: audioAbierto === i }"
      >
        <button type="button" class="lesson-summary" @click="alternarAudio(i)">
          <div class="summary-main">
            <h3>Audio {{ i + 1 }}</h3>
            <p class="summary-desc">{{ au.transcripcion || "Sin transcripción" }}</p>
          </div>
          <span class="chevron">{{ audioAbierto === i ? "▾" : "▸" }}</span>
        </button>
        <div v-if="audioAbierto === i" class="lesson-body">
          <div v-if="grabando && audioGrabando === i" class="grabando-box">
            <span class="grabando-punto"></span>
            <div>
              <strong>Grabando</strong>
              <p>{{ textoGrabacion() }}</p>
            </div>
            <button class="btn grabando-stop" type="button" @click="detenerGrabacion">Detener</button>
          </div>
          <div v-else class="actions">
            <button class="btn ghost" type="button" @click="abrirAudioEscribe(i)">Elegir archivo</button>
            <button class="btn ghost" type="button" @click="grabarAudio(i)">Grabar</button>
            <span v-if="au.audioNombre" class="picked">{{ au.audioNombre }}</span>
          </div>
          <audio v-if="!(grabando && audioGrabando === i) && previewAudio(au)" class="audio-prev" controls :src="previewAudio(au)"></audio>
          <label class="campo-label">Transcripción</label>
          <textarea v-model="au.transcripcion" class="campo-area" rows="2" placeholder="Lo que dice el audio. En la app tiene que escribirse igual."></textarea>
          <button class="btn ghost btn-quitar" type="button" @click="quitarAudio(i)">Quitar</button>
        </div>
      </article>

      <div v-if="escribeSucio || escribeListaLiberar" class="confirm-actions">
        <button v-if="escribeSucio" class="btn" type="button" :disabled="guardandoEscribe" @click="guardarEscribe(false)">
          {{ guardandoEscribe ? "Guardando…" : "Guardar" }}
        </button>
        <button v-if="escribeListaLiberar && !escribeSucio" class="btn" type="button" :disabled="guardandoEscribe" @click="guardarEscribe(true)">
          Liberar a la app
        </button>
      </div>
    </section>

    <section v-else-if="esVocabulario" class="soft-card vacio-seccion">
      <h2 class="box-title">Vocabulario</h2>
      <p class="nivel-hint">Sube la foto y acomódala dentro del círculo, igual que se verá en la app. Debajo van las tres opciones. La primera es la correcta.</p>
      <input ref="fileVocab" class="hidden-file" type="file" accept="image/jpeg,image/png,image/webp" @change="elegirImagenVocab" />

      <label class="campo-label">Imágenes que se muestran en la app</label>
      <input v-model.number="vocabulario.max_items" class="campo-num" type="number" min="1" step="1" />

      <div class="fila-enunciado">
        <button class="btn voc-agregar" type="button" @click="agregarVocab">Agregar imagen</button>
      </div>

      <article
        v-for="(it, i) in vocabulario.items"
        :key="'voc-' + i"
        :ref="'vocab-' + i"
        class="lesson-card enunciado-card"
        :class="{ open: vocabAbierto === i }"
      >
        <button type="button" class="lesson-summary" @click="alternarVocab(i)">
          <div class="summary-main">
            <h3>Imagen {{ i + 1 }}</h3>
            <p class="summary-desc">{{ it.opcion_1 || "Sin opción correcta" }}</p>
          </div>
          <span class="chevron">{{ vocabAbierto === i ? "▾" : "▸" }}</span>
        </button>
        <div v-if="vocabAbierto === i" class="lesson-body">
          <div class="voc-editor">
            <div
              :ref="'voc-marco-' + i"
              class="voc-circulo"
              @mousedown="empezarVoc(i, $event)"
            >
              <img v-if="previewVoc(it)" alt="" draggable="false" :src="previewVoc(it)" :style="estiloVoc(it, i)" @load="medirVoc(it, $event)" />
              <span v-else class="voc-vacio">Sin foto</span>
            </div>
            <div class="voc-lado">
              <button class="btn ghost" type="button" @click="abrirImagenVocab(i)">Elegir imagen</button>
              <p v-if="it.nombre" class="picked">{{ it.nombre }}</p>
              <label class="zoom-label">Zoom {{ it.zoom }}%</label>
              <input v-model.number="it.zoom" class="zoom-range" type="range" min="100" max="250" step="5" />
              <p class="nivel-hint">Arrastra la foto dentro del círculo para centrarla.</p>
            </div>
          </div>
          <label class="campo-label">Opción correcta</label>
          <input v-model="it.opcion_1" class="campo-texto" type="text" placeholder="La palabra de la imagen" />
          <label class="campo-label">Opción 2</label>
          <input v-model="it.opcion_2" class="campo-texto" type="text" />
          <label class="campo-label">Opción 3</label>
          <input v-model="it.opcion_3" class="campo-texto" type="text" />
          <button class="btn ghost btn-quitar" type="button" @click="quitarVocab(i)">Quitar</button>
        </div>
      </article>

      <div v-if="vocabularioSucio || vocabularioListaLiberar" class="confirm-actions">
        <button v-if="vocabularioSucio" class="btn" type="button" :disabled="guardandoVocabulario" @click="guardarVocabulario(false)">
          {{ guardandoVocabulario ? "Guardando…" : "Guardar" }}
        </button>
        <button v-if="vocabularioListaLiberar && !vocabularioSucio" class="btn" type="button" :disabled="guardandoVocabulario" @click="guardarVocabulario(true)">
          Liberar a la app
        </button>
      </div>
    </section>

    <section v-else-if="esClasesGramatica" class="soft-card vacio-seccion">
      <h2 class="box-title">Gramática</h2>
      <p class="nivel-hint">Estas lecciones salen en el tablero de la app. Cada nivel agrega las suyas: al desbloquear este nivel, el alumno ve las de aquí y las de los niveles anteriores. No se califica ni hace falta completarla.</p>
      <div class="fila-enunciado">
        <button class="btn voc-agregar" type="button" @click="agregarClaseGramatica">Agregar lección</button>
      </div>
      <article
        v-for="(c, i) in clasesGramatica.clases"
        :key="'clase-' + i"
        :ref="'clase-' + i"
        class="lesson-card enunciado-card"
        :class="{ open: claseAbierta === i }"
      >
        <button type="button" class="lesson-summary" @click="alternarClaseGramatica(i)">
          <div class="summary-main">
            <h3>{{ c.titulo || ("Lección " + (i + 1)) }}</h3>
            <p class="summary-desc">{{ c.contenido || "Sin texto" }}</p>
          </div>
          <span class="chevron">{{ claseAbierta === i ? "▾" : "▸" }}</span>
        </button>
        <div v-if="claseAbierta === i" class="lesson-body">
          <label class="campo-label">Título</label>
          <input v-model="c.titulo" class="campo-texto" type="text" maxlength="180" placeholder="Nombre de la lección" />
          <label class="campo-label">Texto de la lección</label>
          <textarea v-model="c.contenido" class="campo-area" rows="6" placeholder="La explicación que verá el alumno."></textarea>
          <button class="btn ghost btn-quitar" type="button" @click="quitarClaseGramatica(i)">Quitar</button>
        </div>
      </article>
      <div v-if="clasesSucias || clasesListaLiberar" class="confirm-actions">
        <button v-if="clasesSucias" class="btn" type="button" :disabled="guardandoClases" @click="guardarClasesGramatica(false)">
          {{ guardandoClases ? "Guardando…" : "Guardar" }}
        </button>
        <button v-if="clasesListaLiberar && !clasesSucias" class="btn" type="button" :disabled="guardandoClases" @click="guardarClasesGramatica(true)">
          Liberar a la app
        </button>
      </div>
    </section>

    <section v-else class="soft-card vacio-seccion">
      <h2 class="box-title">{{ seccionActual.titulo }}</h2>
      <p class="nivel-hint">{{ seccionActual.nota }}</p>
    </section>

    <v-dialog v-model="modalAjuste" max-width="520" persistent>
      <v-card v-if="ajuste" class="ajuste-card">
        <h3>Ajustar portada</h3>
        <p>La tarjeta mide lo mismo que en la app (400 px de ancho). La foto se acomoda sola. Si se sale del recuadro, arrástrala para centrar lo que quieras ver.</p>
        <p v-if="errorAjuste" class="faltantes">{{ errorAjuste }}</p>
        <div ref="marcoAjuste" class="app-preview con-foto marco" @mousedown="empezarArrastre">
          <img class="app-preview-foto" alt="" draggable="false" :src="ajuste.src" :style="estiloAjuste()" @load="medirAjuste" />
          <span class="preview-kicker">Video</span>
          <strong>{{ ajuste.leccion.titulo || "Video" }}</strong>
          <p>{{ ajuste.leccion.descripcion || "Sin descripción" }}</p>
          <span class="preview-cta">Empezar</span>
        </div>
        <label class="zoom-label">Zoom {{ ajuste.zoom }}%</label>
        <input v-model.number="ajuste.zoom" class="zoom-range" type="range" min="100" max="250" step="5" />
        <div class="confirm-actions">
          <button class="btn ghost" type="button" :disabled="guardandoAjuste" @click="cerrarAjuste">Cancelar</button>
          <button class="btn" type="button" :disabled="guardandoAjuste" @click="guardarAjuste">
            {{ guardandoAjuste ? "Guardando…" : "Guardar" }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <div v-if="confirmDialog" class="confirm-overlay" @click.self="cerrarConfirm">
      <div class="confirm-card soft-card">
        <p class="confirm-eyebrow">{{ confirmDialog.eyebrow }}</p>
        <h3>{{ confirmDialog.titulo }}</h3>
        <p class="confirm-body">{{ confirmDialog.cuerpo }}</p>
        <div class="confirm-actions">
          <button class="btn ghost" type="button" @click="cerrarConfirm">Cancelar</button>
          <button class="btn" :class="{ danger: confirmDialog.peligro }" type="button" :disabled="confirmDialog.cargando" @click="aceptarConfirm">
            {{ confirmDialog.cargando ? "…" : confirmDialog.aceptar }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MAX_VIDEO = 1024 * 1024 * 1024

function mensaje(e, texto) {
  const data = e && e.response && e.response.data
  if (data && data.message) return data.message
  if (e && e.body && e.body.message) return e.body.message
  return texto
}

export default {
  props: {
    nivel: { type: Object, required: true },
  },
  data() {
    return {
      lecciones: [],
      abierta: null,
      error: "",
      ok: "",
      subiendo: null,
      archivos: {},
      previews: {},
      progreso: {},
      portadas: {},
      portadasLocal: {},
      modalAjuste: false,
      errorAjuste: "",
      ajuste: null,
      arrastre: null,
      guardandoAjuste: false,
      creando: false,
      actualizando: false,
      archivoNueva: null,
      faltantesNueva: [],
      progresoNueva: 0,
      confirmDialog: null,
      nueva: { titulo: "", descripcion: "", orden: 1 },
      seccion: "video",
      secciones: [
        { id: "video", titulo: "Video", nota: "" },
        { id: "gramatica-video", titulo: "Ejercicios de gramática", nota: "Un solo banco de enunciados. La app mezcla elegir y arrastrar." },
        { id: "habla", titulo: "Habla", nota: "Se carga el audio y la respuesta correcta. En la app el alumno lo escucha, lo graba y Whisper califica esa toma." },
        { id: "escribe", titulo: "Escribe", nota: "Aquí se carga el audio y la transcripción para validarla." },
        { id: "vocabulario", titulo: "Vocabulario", nota: "Aquí se carga la imagen en círculo y las tres opciones." },
        { id: "gramatica-clases", titulo: "Gramática", nota: "Lecciones de gramática de este nivel. En la app se ven junto con las de los niveles anteriores. No hace falta completarlas." },
      ],
      gramatica: {
        banco: { explicacion: "", max_ejercicios: 1, ejercicios: [], liberado: 0, mensajes: null },
      },
      mensajesAbiertos: false,
      gruposMensaje: [
        { id: "reprobado", etiqueta: "Menos de 6" },
        { id: "seis", etiqueta: "De 6 a 7.9" },
        { id: "ocho", etiqueta: "De 8 a 9.9" },
        { id: "diez", etiqueta: "10" },
      ],
      enunciadoNuevo: "",
      enunciadoAbierto: null,
      escribe: { max_audios: 1, audios: [], liberado: 0 },
      escribeFirma: "",
      escribeListaLiberar: false,
      habla: { max_audios: 1, audios: [], liberado: 0 },
      hablaFirma: "",
      hablaListaLiberar: false,
      hablaAbierto: null,
      hablaDestino: null,
      guardandoHabla: false,
      audioAbierto: null,
      audioDestino: null,
      grabando: false,
      audioGrabando: null,
      grabacionSegundos: 0,
      guardandoEscribe: false,
      vocabulario: { max_items: 1, items: [], liberado: 0 },
      vocabularioFirma: "",
      vocabularioListaLiberar: false,
      vocabAbierto: null,
      vocabDestino: null,
      guardandoVocabulario: false,
      gramaticaFirma: "",
      gramaticaListaLiberar: false,
      guardandoGramatica: false,
      clasesGramatica: { clases: [] },
      clasesFirma: "",
      clasesListaLiberar: false,
      claseAbierta: null,
      guardandoClases: false,
    }
  },
  computed: {
    marca() {
      return String(this.nivel.marca || "").toUpperCase()
    },
    subtitulo() {
      if (this.nivel.subtitulo_prefijo) return this.nivel.subtitulo_prefijo
      return String(this.nivel.subtitulo || "Desde cero").split("·")[0].trim() || "Desde cero"
    },
    estadoLabel() {
      return this.nivel.estado === "publicado" ? "Publicado (se puede comprar)" : "Próximamente"
    },
    seccionActual() {
      const id = this.seccion
      const lista = this.secciones
      for (let i = 0; i < lista.length; i++) {
        if (lista[i].id === id) return lista[i]
      }
      return lista[0]
    },
    esGramatica() {
      return this.seccion === "gramatica-video"
    },
    esHabla() {
      return this.seccion === "habla"
    },
    esEscribe() {
      return this.seccion === "escribe"
    },
    esVocabulario() {
      return this.seccion === "vocabulario"
    },
    esClasesGramatica() {
      return this.seccion === "gramatica-clases"
    },
    vocabularioSucio() {
      return this.firmaDeVocabulario() !== this.vocabularioFirma
    },
    escribeSucio() {
      return this.firmaDeEscribe() !== this.escribeFirma
    },
    hablaSucio() {
      return this.firmaDeHabla() !== this.hablaFirma
    },
    moduloGramatica() {
      return this.gramatica.banco
    },
    gramaticaSucia() {
      return this.firmaDeGramatica() !== this.gramaticaFirma
    },
    clasesSucias() {
      return this.firmaDeClases() !== this.clasesFirma
    },
  },
  watch: {
    seccion(id) {
      this.detenerGrabacion()
      if (id === "gramatica-video") this.cargarGramatica()
      if (id === "habla") this.cargarHabla()
      if (id === "escribe") this.cargarEscribe()
      if (id === "vocabulario") this.cargarVocabulario()
      if (id === "gramatica-clases") this.cargarClasesGramatica()
    },
  },
  created() {
    this.gramatica.banco.mensajes = this.mensajesBase()
    this.cargar()
  },
    beforeDestroy() {
      this.pararRelojGrabacion()
      this.detenerGrabacion()
      this.soltarArrastre()
      this.soltarVoc()
    const urls = this.previews
    Object.keys(urls).forEach((id) => {
      if (urls[id]) URL.revokeObjectURL(urls[id])
    })
  },
  methods: {
    mensajesBase() {
      return {
        reprobado: { titulo: "Vas a poder con esto", mensaje: "Todavía no salió, y no pasa nada. Con práctica y un poco de esfuerzo este tema se te va a facilitar. Cuando quieras, lo vuelves a intentar." },
        seis: { titulo: "Bien encaminado", mensaje: "Lo hiciste bien. Ya va tomando forma, y con un repaso más lo puedes dejar redondo." },
        ocho: { titulo: "Ya se te da", mensaje: "La mayoría te salió bien. Ya entendiste el tema; se te ve suelto." },
        diez: { titulo: "Qué buen trabajo", mensaje: "Lo resolviste completo. Se nota el cuidado que le pusiste, y así se siente cuando ya lo traes bien puesto." },
      }
    },
    completarMensajes(origen) {
      const base = this.mensajesBase()
      const datos = origen || {}
      const claves = ["reprobado", "seis", "ocho", "diez"]
      for (let i = 0; i < claves.length; i++) {
        const clave = claves[i]
        const item = datos[clave] || {}
        if (item.titulo) base[clave].titulo = item.titulo
        if (item.mensaje) base[clave].mensaje = item.mensaje
      }
      return base
    },
    moduloVacio() {
      return { explicacion: "", max_ejercicios: 1, ejercicios: [], liberado: 0, mensajes: this.mensajesBase() }
    },
    palabrasDe(texto) {
      return String(texto || "").trim().split(/\s+/).filter(function (p) { return p })
    },
    enunciadoConBlanco(ej) {
      const partes = this.palabrasDe(ej.enunciado)
      const i = ej.indice
      if (i == null || i < 0 || i >= partes.length) return ej.enunciado
      const copia = partes.slice()
      copia[i] = "______"
      return copia.join(" ")
    },
    firmaDeGramatica() {
      const m = this.moduloGramatica || {}
      const ejercicios = (m.ejercicios || []).map(function (ej) {
        const opciones = ej.opciones || []
        return {
          enunciado: String(ej.enunciado || ""),
          indice: ej.indice == null ? null : Number(ej.indice),
          opciones: [String(opciones[0] || ""), String(opciones[1] || ""), String(opciones[2] || "")],
        }
      })
      return JSON.stringify({
        explicacion: String(m.explicacion || ""),
        max_ejercicios: Number(m.max_ejercicios) || 1,
        mensajes: this.completarMensajes(m.mensajes),
        ejercicios: ejercicios,
      })
    },
    tomarFirmaGramatica() {
      this.gramaticaFirma = this.firmaDeGramatica()
    },
    alternarEnunciado(i) {
      const abrir = this.enunciadoAbierto !== i
      this.enunciadoAbierto = abrir ? i : null
      if (abrir) this.acomodarEnunciado(i)
    },
    acomodarEnunciado(i) {
      this.acomodarBloque("enunciado-" + i)
    },
    acomodarBloque(ref) {
      const self = this
      this.$nextTick(function () {
        const nodo = self.$refs[ref]
        const el = nodo && nodo[0] ? nodo[0] : nodo
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    },
    agregarEnunciado() {
      const texto = String(this.enunciadoNuevo || "").trim()
      if (!texto) return
      this.moduloGramatica.ejercicios.push({
        enunciado: texto,
        indice: null,
        opciones: ["", "", ""],
      })
      this.enunciadoAbierto = this.moduloGramatica.ejercicios.length - 1
      this.enunciadoNuevo = ""
      this.acomodarEnunciado(this.enunciadoAbierto)
    },
    quitarEnunciado(i) {
      this.moduloGramatica.ejercicios.splice(i, 1)
      if (this.enunciadoAbierto === i) this.enunciadoAbierto = null
      else if (this.enunciadoAbierto != null && this.enunciadoAbierto > i) this.enunciadoAbierto = this.enunciadoAbierto - 1
    },
    alEditarEnunciado(ej) {
      const partes = this.palabrasDe(ej.enunciado)
      const correcta = String((ej.opciones && ej.opciones[0]) || "")
      if (ej.indice != null && ej.indice < partes.length && partes[ej.indice] === correcta) return
      let hallado = -1
      for (let i = 0; i < partes.length; i++) {
        if (partes[i] === correcta) {
          hallado = i
          break
        }
      }
      ej.indice = hallado === -1 ? null : hallado
    },
    elegirPalabra(ej, indice) {
      const partes = this.palabrasDe(ej.enunciado)
      const palabra = partes[indice] || ""
      const anterior = ej.indice == null ? "" : (partes[ej.indice] || "")
      if (!ej.opciones[0] || ej.opciones[0] === anterior) {
        this.$set(ej.opciones, 0, palabra)
      }
      ej.indice = indice
    },
    cargarGramatica() {
      const self = this
      if (!this.nivel || !this.nivel.id) return
      this.$http.get("curso.virtual.gramatica/" + this.nivel.id, { params: { _: Date.now() } }).then(function (r) {
        const data = r.data || r.body || {}
        const modulos = data.modulos || {}
        const banco = modulos.banco || self.moduloVacio()
        banco.mensajes = self.completarMensajes(banco.mensajes)
        if (!banco.ejercicios) banco.ejercicios = []
        banco.ejercicios.forEach(function (ej) {
          if (!ej.opciones || ej.opciones.length < 3) ej.opciones = ["", "", ""]
        })
        self.gramatica.banco = banco
        self.enunciadoAbierto = null
        self.gramaticaListaLiberar = false
        self.tomarFirmaGramatica()
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo cargar la gramática")
      })
    },
    guardarGramatica(liberar) {
      const m = this.moduloGramatica
      const max = Number(m.max_ejercicios)
      if (!Number.isFinite(max) || max < 1) {
        this.error = "Indica cuántos ejercicios se muestran en la app"
        return
      }
      if (m.ejercicios.length && max > m.ejercicios.length) {
        this.error = "Ese máximo es mayor que los enunciados que cargaste"
        return
      }
      if (liberar && !m.ejercicios.length) {
        this.error = "Agrega enunciados antes de liberar a la app"
        return
      }
      for (let i = 0; i < m.ejercicios.length; i++) {
        const ej = m.ejercicios[i]
        const partes = this.palabrasDe(ej.enunciado)
        if (ej.indice == null || ej.indice < 0 || ej.indice >= partes.length) {
          this.error = "En el enunciado " + (i + 1) + " elige la palabra en blanco"
          return
        }
        const o1 = String(ej.opciones[0] || "").trim()
        const o2 = String(ej.opciones[1] || "").trim()
        const o3 = String(ej.opciones[2] || "").trim()
        if (!o1 || !o2 || !o3) {
          this.error = "El enunciado " + (i + 1) + " necesita 3 palabras"
          return
        }
      }
      this.guardandoGramatica = true
      this.error = ""
      this.$http.put("curso.virtual.gramatica/" + this.nivel.id, {
        tipo: "banco",
        explicacion: m.explicacion || "",
        max_ejercicios: max,
        mensajes: this.completarMensajes(m.mensajes),
        ejercicios: m.ejercicios,
        liberar: !!liberar,
      }).then((r) => {
        const data = r.data || r.body || {}
        if (liberar || data.liberado) m.liberado = 1
        this.tomarFirmaGramatica()
        this.gramaticaListaLiberar = !liberar
        this.ok = liberar ? "Listo. Ya se ve en la app." : "Gramática guardada. Ya puedes liberarla a la app."
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo guardar la gramática")
      }).finally(() => {
        this.guardandoGramatica = false
      })
    },
    firmaDeEscribe() {
      const m = this.escribe || {}
      const audios = (m.audios || []).map(function (au) {
        return {
          transcripcion: String(au.transcripcion || ""),
          audio_ruta: String(au.audio_ruta || ""),
          audioNombre: String(au.audioNombre || ""),
        }
      })
      return JSON.stringify({
        max_audios: Number(m.max_audios) || 1,
        audios: audios,
      })
    },
    tomarFirmaEscribe() {
      this.escribeFirma = this.firmaDeEscribe()
    },
    textoGrabacion() {
      const s = this.grabacionSegundos || 0
      const m = Math.floor(s / 60)
      const r = s % 60
      return m + ":" + (r < 10 ? "0" : "") + r
    },
    alternarAudio(i) {
      if (this.grabando && this.audioGrabando === i && this.audioAbierto === i) return
      const abrir = this.audioAbierto !== i
      this.audioAbierto = abrir ? i : null
      if (abrir) this.acomodarBloque("audio-" + i)
    },
    agregarAudio() {
      this.escribe.audios.push({
        transcripcion: "",
        audio_ruta: "",
        audioNombre: "",
        preview: "",
        pendiente: null,
      })
      this.audioAbierto = this.escribe.audios.length - 1
      this.acomodarBloque("audio-" + this.audioAbierto)
    },
    quitarAudio(i) {
      const au = this.escribe.audios[i]
      if (au && au.preview) URL.revokeObjectURL(au.preview)
      this.escribe.audios.splice(i, 1)
      if (this.audioAbierto === i) this.audioAbierto = null
      else if (this.audioAbierto != null && this.audioAbierto > i) this.audioAbierto = this.audioAbierto - 1
    },
    abrirAudioEscribe(i) {
      this.audioDestino = i
      if (this.$refs.fileEscribe) this.$refs.fileEscribe.click()
    },
    elegirAudioEscribe(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ""
      if (!file || this.audioDestino == null) return
      this.ponerAudioLocal(this.escribe.audios[this.audioDestino], file)
    },
    ponerAudioLocal(au, archivo) {
      if (!au) return
      if (au.preview) URL.revokeObjectURL(au.preview)
      au.pendiente = archivo
      au.audioNombre = archivo.name || "grabacion.webm"
      au.preview = URL.createObjectURL(archivo)
    },
    previewAudio(au) {
      if (!au) return ""
      if (au.preview) return au.preview
      if (au.id && au.audio_ruta) return this.urlAudioGuardado(au.id)
      return ""
    },
    urlAudioGuardado(id) {
      const base = (this.$http.defaults && this.$http.defaults.baseURL) || "/api-rh/"
      const token = window.localStorage.getItem("accessToken") || window.localStorage.getItem("tlaKey") || ""
      const root = String(base).charAt(String(base).length - 1) === "/" ? String(base) : String(base) + "/"
      return root + "curso.virtual.escribe.audio/" + id + "?token=" + encodeURIComponent(token)
    },
    grabarAudio(i) {
      if (this.grabando && this.audioGrabando === i) {
        this.detenerGrabacion()
        return
      }
      if (this.grabando) this.detenerGrabacion()
      const self = this
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = "Este navegador no puede grabar audio"
        return
      }
      navigator.mediaDevices.getUserMedia({ audio: true }).then(function (stream) {
        const rec = new MediaRecorder(stream)
        const partes = []
        rec.ondataavailable = function (ev) {
          if (ev.data && ev.data.size) partes.push(ev.data)
        }
        rec.onstop = function () {
          stream.getTracks().forEach(function (t) { t.stop() })
          const tipo = rec.mimeType || "audio/webm"
          const blob = new Blob(partes, { type: tipo })
          const archivo = new File([blob], "grabacion.webm", { type: tipo })
          self.ponerAudioLocal(self.escribe.audios[i], archivo)
          if (self.escribe.audios[i]) self.escribe.audios[i].audioNombre = "Grabación"
          self.pararRelojGrabacion()
          self.grabando = false
          self.audioGrabando = null
          self._rec = null
        }
        self._rec = rec
        self._streamGrab = stream
        self.grabacionSegundos = 0
        self.pararRelojGrabacion()
        self._relojGrab = setInterval(function () {
          self.grabacionSegundos = self.grabacionSegundos + 1
        }, 1000)
        self.grabando = true
        self.audioGrabando = i
        rec.start()
      }).catch(function () {
        self.error = "No se pudo usar el micrófono"
      })
    },
    pararRelojGrabacion() {
      if (this._relojGrab) clearInterval(this._relojGrab)
      this._relojGrab = null
    },
    detenerGrabacion() {
      if (this._rec && this._rec.state !== "inactive") {
        this._rec.stop()
        return
      }
      this.pararRelojGrabacion()
      if (this._streamGrab) {
        this._streamGrab.getTracks().forEach(function (t) { t.stop() })
        this._streamGrab = null
      }
      this.grabando = false
      this.audioGrabando = null
    },
    firmaDeHabla() {
      const m = this.habla || {}
      const audios = (m.audios || []).map(function (au) {
        return {
          transcripcion: String(au.transcripcion || "").trim(),
          audio_ruta: au.audio_ruta || "",
          audioNombre: au.audioNombre || "",
        }
      })
      return JSON.stringify({ max_audios: Number(m.max_audios) || 1, audios: audios })
    },
    tomarFirmaHabla() {
      this.hablaFirma = this.firmaDeHabla()
    },
    alternarHabla(i) {
      if (this.grabando && this.audioGrabando === i && this.hablaAbierto === i) return
      const abrir = this.hablaAbierto !== i
      this.hablaAbierto = abrir ? i : null
      if (abrir) this.acomodarBloque("habla-" + i)
    },
    agregarHabla() {
      this.habla.audios.push({ transcripcion: "", audio_ruta: "", audioNombre: "", preview: "", pendiente: null })
      this.hablaAbierto = this.habla.audios.length - 1
      this.acomodarBloque("habla-" + this.hablaAbierto)
    },
    quitarHabla(i) {
      const au = this.habla.audios[i]
      if (au && au.preview) URL.revokeObjectURL(au.preview)
      this.habla.audios.splice(i, 1)
      if (this.hablaAbierto === i) this.hablaAbierto = null
    },
    abrirAudioHabla(i) {
      this.hablaDestino = i
      if (this.$refs.fileHabla) this.$refs.fileHabla.click()
    },
    elegirAudioHabla(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ""
      if (!file || this.hablaDestino == null) return
      this.ponerAudioLocal(this.habla.audios[this.hablaDestino], file)
    },
    previewHabla(au) {
      if (!au) return ""
      if (au.preview) return au.preview
      if (au.id && au.audio_ruta) return this.urlAudioHabla(au.id)
      return ""
    },
    urlAudioHabla(id) {
      const base = (this.$http.defaults && this.$http.defaults.baseURL) || "/api-rh/"
      const token = window.localStorage.getItem("accessToken") || window.localStorage.getItem("tlaKey") || ""
      const root = String(base).charAt(String(base).length - 1) === "/" ? String(base) : String(base) + "/"
      return root + "curso.virtual.habla.audio/" + id + "?token=" + encodeURIComponent(token)
    },
    grabarHabla(i) {
      if (this.grabando && this.audioGrabando === i) {
        this.detenerGrabacion()
        return
      }
      if (this.grabando) this.detenerGrabacion()
      const self = this
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = "Este navegador no puede grabar audio"
        return
      }
      navigator.mediaDevices.getUserMedia({ audio: true }).then(function (stream) {
        const rec = new MediaRecorder(stream)
        const partes = []
        rec.ondataavailable = function (ev) {
          if (ev.data && ev.data.size) partes.push(ev.data)
        }
        rec.onstop = function () {
          stream.getTracks().forEach(function (t) { t.stop() })
          const tipo = rec.mimeType || "audio/webm"
          const archivo = new File([blobDe(partes, tipo)], "grabacion.webm", { type: tipo })
          self.ponerAudioLocal(self.habla.audios[i], archivo)
          if (self.habla.audios[i]) self.habla.audios[i].audioNombre = "Grabación"
          self.pararRelojGrabacion()
          self.grabando = false
          self.audioGrabando = null
          self._rec = null
        }
        function blobDe(partes, tipo) {
          return new Blob(partes, { type: tipo })
        }
        self._rec = rec
        self._streamGrab = stream
        self.grabacionSegundos = 0
        self.pararRelojGrabacion()
        self._relojGrab = setInterval(function () {
          self.grabacionSegundos = self.grabacionSegundos + 1
        }, 1000)
        self.grabando = true
        self.audioGrabando = i
        rec.start()
      }).catch(function () {
        self.error = "No se pudo usar el micrófono"
      })
    },
    cargarHabla(listaLiberar) {
      const self = this
      if (!this.nivel || !this.nivel.id) return
      this.$http.get("curso.virtual.habla/" + this.nivel.id, { params: { _: Date.now() } }).then(function (r) {
        const data = r.data || r.body || {}
        self.habla = {
          max_audios: data.max_audios || 1,
          liberado: data.liberado || 0,
          audios: (data.audios || []).map(function (au) {
            return {
              id: au.id,
              transcripcion: au.transcripcion || "",
              audio_ruta: au.audio_ruta || "",
              audioNombre: "",
              preview: "",
              pendiente: null,
            }
          }),
        }
        self.hablaAbierto = null
        self.tomarFirmaHabla()
        self.hablaListaLiberar = !!listaLiberar
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo cargar Habla")
      })
    },
    guardarHabla(liberar) {
      const m = this.habla
      const max = Number(m.max_audios)
      if (!Number.isFinite(max) || max < 1) {
        this.error = "Indica cuántos audios se muestran en la app"
        return
      }
      if (m.audios.length && max > m.audios.length) {
        this.error = "Ese máximo es mayor que los audios que cargaste"
        return
      }
      for (let i = 0; i < m.audios.length; i++) {
        if (!String(m.audios[i].transcripcion || "").trim()) {
          this.error = "El audio " + (i + 1) + " necesita la respuesta correcta"
          return
        }
        if (!m.audios[i].audio_ruta && !m.audios[i].pendiente) {
          this.error = "El audio " + (i + 1) + " necesita un archivo o una grabación"
          return
        }
      }
      this.guardandoHabla = true
      this.error = ""
      const self = this
      const subir = m.audios.map(function (au) {
        if (!au.pendiente) return Promise.resolve(au)
        const form = new FormData()
        form.append("audio", au.pendiente, au.audioNombre || "audio.webm")
        return self.$http.post("curso.virtual.habla.audio/" + self.nivel.id, form).then(function (r) {
          const data = r.data || r.body || {}
          au.audio_ruta = data.audio_ruta || ""
          au.pendiente = null
          return au
        })
      })
      Promise.all(subir).then(function () {
        return self.$http.put("curso.virtual.habla/" + self.nivel.id, {
          max_audios: max,
          liberar: !!liberar,
          audios: m.audios.map(function (au) {
            return { transcripcion: au.transcripcion, audio_ruta: au.audio_ruta }
          }),
        })
      }).then(function () {
        self.ok = liberar ? "Listo. Ya se ve en la app." : "Habla guardado. Ya puedes liberarlo a la app."
        self.cargarHabla(!liberar)
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo guardar Habla")
      }).finally(function () {
        self.guardandoHabla = false
      })
    },
    cargarEscribe(listaLiberar) {
      const self = this
      if (!this.nivel || !this.nivel.id) return
      this.$http.get("curso.virtual.escribe/" + this.nivel.id, { params: { _: Date.now() } }).then(function (r) {
        const data = r.data || r.body || {}
        const audios = (data.audios || []).map(function (au) {
          return {
            id: au.id,
            transcripcion: au.transcripcion || "",
            audio_ruta: au.audio_ruta || "",
            audioNombre: "",
            preview: "",
            pendiente: null,
          }
        })
        self.escribe = {
          max_audios: data.max_audios || 1,
          liberado: data.liberado || 0,
          audios: audios,
        }
        self.audioAbierto = null
        self.tomarFirmaEscribe()
        self.escribeListaLiberar = !!listaLiberar
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo cargar Escribe")
      })
    },
    guardarEscribe(liberar) {
      const m = this.escribe
      const max = Number(m.max_audios)
      if (!Number.isFinite(max) || max < 1) {
        this.error = "Indica cuántos audios se muestran en la app"
        return
      }
      if (m.audios.length && max > m.audios.length) {
        this.error = "Ese máximo es mayor que los audios que cargaste"
        return
      }
      if (liberar && !m.audios.length) {
        this.error = "Agrega audios antes de liberar a la app"
        return
      }
      for (let i = 0; i < m.audios.length; i++) {
        const au = m.audios[i]
        if (!String(au.transcripcion || "").trim()) {
          this.error = "El audio " + (i + 1) + " necesita la transcripción"
          return
        }
        if (!au.audio_ruta && !au.pendiente) {
          this.error = "El audio " + (i + 1) + " necesita un archivo o una grabación"
          return
        }
      }
      this.guardandoEscribe = true
      this.error = ""
      const self = this
      const subir = m.audios.map(function (au) {
        if (!au.pendiente) return Promise.resolve(au)
        const form = new FormData()
        form.append("audio", au.pendiente, au.audioNombre || "audio.webm")
        return self.$http.post("curso.virtual.escribe.audio/" + self.nivel.id, form).then(function (r) {
          const data = r.data || r.body || {}
          au.audio_ruta = data.audio_ruta || ""
          au.pendiente = null
          return au
        })
      })
      Promise.all(subir).then(function () {
        return self.$http.put("curso.virtual.escribe/" + self.nivel.id, {
          max_audios: max,
          liberar: !!liberar,
          audios: m.audios.map(function (au) {
            return {
              transcripcion: au.transcripcion,
              audio_ruta: au.audio_ruta,
            }
          }),
        })
      }).then(function () {
        self.ok = liberar ? "Listo. Ya se ve en la app." : "Escribe guardado. Ya puedes liberarlo a la app."
        self.cargarEscribe(!liberar)
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo guardar Escribe")
      }).finally(function () {
        self.guardandoEscribe = false
      })
    },
    firmaDeVocabulario() {
      const m = this.vocabulario || {}
      const items = (m.items || []).map(function (it) {
        return {
          imagen_ruta: String(it.imagen_ruta || ""),
          nombre: String(it.nombre || ""),
          x: Number(it.x) || 0,
          y: Number(it.y) || 0,
          zoom: Number(it.zoom) || 100,
          opcion_1: String(it.opcion_1 || ""),
          opcion_2: String(it.opcion_2 || ""),
          opcion_3: String(it.opcion_3 || ""),
        }
      })
      return JSON.stringify({ max_items: Number(m.max_items) || 1, items: items })
    },
    tomarFirmaVocabulario() {
      this.vocabularioFirma = this.firmaDeVocabulario()
    },
    alternarVocab(i) {
      const abrir = this.vocabAbierto !== i
      this.vocabAbierto = abrir ? i : null
      if (abrir) this.acomodarBloque("vocab-" + i)
    },
    agregarVocab() {
      this.vocabulario.items.push({
        imagen_ruta: "",
        x: 50,
        y: 50,
        zoom: 100,
        opcion_1: "",
        opcion_2: "",
        opcion_3: "",
        preview: "",
        pendiente: null,
        nombre: "",
        nw: 0,
        nh: 0,
      })
      this.vocabAbierto = this.vocabulario.items.length - 1
      this.acomodarBloque("vocab-" + this.vocabAbierto)
    },
    quitarVocab(i) {
      const it = this.vocabulario.items[i]
      if (it && it.preview) URL.revokeObjectURL(it.preview)
      this.vocabulario.items.splice(i, 1)
      if (this.vocabAbierto === i) this.vocabAbierto = null
      else if (this.vocabAbierto != null && this.vocabAbierto > i) this.vocabAbierto = this.vocabAbierto - 1
    },
    abrirImagenVocab(i) {
      this.vocabDestino = i
      if (this.$refs.fileVocab) this.$refs.fileVocab.click()
    },
    elegirImagenVocab(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ""
      if (!file || this.vocabDestino == null) return
      if (file.size > 5 * 1024 * 1024) {
        this.error = "La imagen no puede pasar de 5 MB"
        return
      }
      this.ponerImagenVocab(this.vocabulario.items[this.vocabDestino], file)
    },
    ponerImagenVocab(it, archivo) {
      if (!it) return
      if (it.preview) URL.revokeObjectURL(it.preview)
      it.pendiente = archivo
      it.nombre = archivo.name || "imagen"
      it.preview = URL.createObjectURL(archivo)
      it.nw = 0
      it.nh = 0
    },
    previewVoc(it) {
      if (!it) return ""
      if (it.preview) return it.preview
      if (it.id && it.imagen_ruta) return this.urlImagenVocab(it.id)
      return ""
    },
    urlImagenVocab(id) {
      const base = (this.$http.defaults && this.$http.defaults.baseURL) || "/api-rh/"
      const token = window.localStorage.getItem("accessToken") || window.localStorage.getItem("tlaKey") || ""
      const root = String(base).charAt(String(base).length - 1) === "/" ? String(base) : String(base) + "/"
      return root + "curso.virtual.vocabulario.imagen/" + id + "?token=" + encodeURIComponent(token)
    },
    medirVoc(it, e) {
      const img = e.target
      if (!img || !img.naturalWidth) return
      it.nw = img.naturalWidth
      it.nh = img.naturalHeight
      this.$forceUpdate()
    },
    marcoVoc(i) {
      const ref = this.$refs["voc-marco-" + i]
      return Array.isArray(ref) ? ref[0] : ref
    },
    estiloVoc(it, i) {
      const caja = this.cajaDe(this.marcoVoc(i), it.nw, it.nh, it.x, it.y, it.zoom)
      if (!caja) return { width: "100%", height: "100%", left: "0", top: "0", objectFit: "cover" }
      return { width: caja.width, height: caja.height, left: caja.left, top: caja.top, objectFit: "fill", transform: "none" }
    },
    empezarVoc(i, e) {
      const it = this.vocabulario.items[i]
      if (!it || !it.nw || e.button !== 0) return
      e.preventDefault()
      const marco = this.marcoVoc(i)
      const caja = this.cajaDe(marco, it.nw, it.nh, it.x, it.y, it.zoom)
      this._vocArrastre = {
        i: i,
        x: e.clientX,
        y: e.clientY,
        zoom: it.zoom,
        left: caja ? caja.leftPx : 0,
        top: caja ? caja.topPx : 0,
      }
      window.addEventListener("mousemove", this.moverVoc)
      window.addEventListener("mouseup", this.soltarVoc)
    },
    moverVoc(e) {
      const arr = this._vocArrastre
      if (!arr) return
      const it = this.vocabulario.items[arr.i]
      const marco = this.marcoVoc(arr.i)
      if (!it || !marco || !it.nw) return
      let zoom = arr.zoom
      let left = arr.left + (e.clientX - arr.x)
      let top = arr.top + (e.clientY - arr.y)
      const holgura = () => this.cajaDe(marco, it.nw, it.nh, 0, 0, zoom) || { slackX: 0, slackY: 0 }
      let m = holgura()
      let guard = 0
      while (guard < 30 && zoom < 250 && (left < -m.slackX - 8 || left > 8 || top < -m.slackY - 8 || top > 8)) {
        const prevX = m.slackX
        const prevY = m.slackY
        zoom += 5
        m = holgura()
        left -= (m.slackX - prevX) / 2
        top -= (m.slackY - prevY) / 2
        guard += 1
      }
      left = Math.max(-m.slackX, Math.min(0, left))
      top = Math.max(-m.slackY, Math.min(0, top))
      it.zoom = zoom
      it.x = m.slackX > 1 ? Math.round((-left / m.slackX) * 100) : 50
      it.y = m.slackY > 1 ? Math.round((-top / m.slackY) * 100) : 50
    },
    soltarVoc() {
      this._vocArrastre = null
      window.removeEventListener("mousemove", this.moverVoc)
      window.removeEventListener("mouseup", this.soltarVoc)
    },
    cargarVocabulario(listaLiberar) {
      const self = this
      if (!this.nivel || !this.nivel.id) return
      this.$http.get("curso.virtual.vocabulario/" + this.nivel.id, { params: { _: Date.now() } }).then(function (r) {
        const data = r.data || r.body || {}
        self.vocabulario = {
          max_items: data.max_items || 1,
          liberado: data.liberado || 0,
          items: (data.items || []).map(function (it) {
            return {
              id: it.id,
              imagen_ruta: it.imagen_ruta || "",
              x: it.x == null ? 50 : Number(it.x),
              y: it.y == null ? 50 : Number(it.y),
              zoom: it.zoom == null ? 100 : Number(it.zoom),
              opcion_1: it.opcion_1 || "",
              opcion_2: it.opcion_2 || "",
              opcion_3: it.opcion_3 || "",
              preview: "",
              pendiente: null,
              nombre: "",
              nw: 0,
              nh: 0,
            }
          }),
        }
        self.vocabAbierto = null
        self.tomarFirmaVocabulario()
        self.vocabularioListaLiberar = !!listaLiberar
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo cargar Vocabulario")
      })
    },
    guardarVocabulario(liberar) {
      const m = this.vocabulario
      const max = Number(m.max_items)
      if (!Number.isFinite(max) || max < 1) {
        this.error = "Indica cuántas imágenes se muestran en la app"
        return
      }
      if (m.items.length && max > m.items.length) {
        this.error = "Ese máximo es mayor que las imágenes que cargaste"
        return
      }
      for (let i = 0; i < m.items.length; i++) {
        const it = m.items[i]
        if (!String(it.opcion_1 || "").trim() || !String(it.opcion_2 || "").trim() || !String(it.opcion_3 || "").trim()) {
          this.error = "La imagen " + (i + 1) + " necesita 3 opciones"
          return
        }
        if (!it.imagen_ruta && !it.pendiente) {
          this.error = "La imagen " + (i + 1) + " necesita una foto"
          return
        }
      }
      this.guardandoVocabulario = true
      this.error = ""
      const self = this
      const subir = m.items.map(function (it) {
        if (!it.pendiente) return Promise.resolve(it)
        const form = new FormData()
        form.append("imagen", it.pendiente, it.nombre || "imagen.jpg")
        return self.$http.post("curso.virtual.vocabulario.imagen/" + self.nivel.id, form).then(function (r) {
          const data = r.data || r.body || {}
          it.imagen_ruta = data.imagen_ruta || ""
          it.pendiente = null
          return it
        })
      })
      Promise.all(subir).then(function () {
        return self.$http.put("curso.virtual.vocabulario/" + self.nivel.id, {
          max_items: max,
          liberar: !!liberar,
          items: m.items.map(function (it) {
            return {
              imagen_ruta: it.imagen_ruta,
              x: it.x,
              y: it.y,
              zoom: it.zoom,
              opcion_1: it.opcion_1,
              opcion_2: it.opcion_2,
              opcion_3: it.opcion_3,
            }
          }),
        })
      }).then(function () {
        self.ok = liberar ? "Listo. Ya se ve en la app." : "Vocabulario guardado. Ya puedes liberarlo a la app."
        self.cargarVocabulario(!liberar)
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo guardar Vocabulario")
      }).finally(function () {
        self.guardandoVocabulario = false
      })
    },
    firmaDeClases() {
      const lista = (this.clasesGramatica && this.clasesGramatica.clases) || []
      return JSON.stringify(lista.map(function (c) {
        return { titulo: String(c.titulo || "").trim(), contenido: String(c.contenido || "").trim() }
      }))
    },
    tomarFirmaClases() {
      this.clasesFirma = this.firmaDeClases()
    },
    agregarClaseGramatica() {
      this.clasesGramatica.clases.push({ titulo: "", contenido: "" })
      this.claseAbierta = this.clasesGramatica.clases.length - 1
      const self = this
      this.$nextTick(function () { self.acomodarBloque("clase-" + self.claseAbierta) })
    },
    alternarClaseGramatica(i) {
      this.claseAbierta = this.claseAbierta === i ? null : i
      if (this.claseAbierta != null) {
        const self = this
        this.$nextTick(function () { self.acomodarBloque("clase-" + i) })
      }
    },
    quitarClaseGramatica(i) {
      this.clasesGramatica.clases.splice(i, 1)
      this.claseAbierta = null
    },
    cargarClasesGramatica(listaLiberar) {
      const self = this
      if (!this.nivel || !this.nivel.id) return
      this.$http.get("curso.virtual.gramatica.clases/" + this.nivel.id, { params: { _: Date.now() } }).then(function (r) {
        const data = r.data || r.body || {}
        self.clasesGramatica = {
          clases: (data.clases || []).map(function (c) {
            return { titulo: c.titulo || "", contenido: c.contenido || "" }
          }),
        }
        self.claseAbierta = null
        self.tomarFirmaClases()
        self.clasesListaLiberar = !!listaLiberar
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo cargar Gramática")
      })
    },
    guardarClasesGramatica(liberar) {
      const lista = this.clasesGramatica.clases
      for (let i = 0; i < lista.length; i++) {
        if (!String(lista[i].titulo || "").trim() || !String(lista[i].contenido || "").trim()) {
          this.error = "La lección " + (i + 1) + " necesita título y texto"
          return
        }
      }
      if (liberar && !lista.length) {
        this.error = "Agrega lecciones antes de liberar a la app"
        return
      }
      this.guardandoClases = true
      this.error = ""
      const self = this
      this.$http.put("curso.virtual.gramatica.clases/" + this.nivel.id, {
        liberar: !!liberar,
        clases: lista.map(function (c) {
          return { titulo: c.titulo, contenido: c.contenido }
        }),
      }).then(function () {
        self.ok = liberar ? "Listo. Ya se ve en la app." : "Gramática guardada. Ya puedes liberarla a la app."
        self.cargarClasesGramatica(!liberar)
      }).catch(function (e) {
        self.error = mensaje(e, "No se pudo guardar Gramática")
      }).finally(function () {
        self.guardandoClases = false
      })
    },
    peso(n) {
      if (!n) return ""
      if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB"
      return (n / (1024 * 1024)).toFixed(1) + " MB"
    },
    formatearDuracion(seg) {
      const s = Number(seg) || 0
      if (s <= 0) return "—"
      const m = Math.floor(s / 60)
      const r = s % 60
      if (m <= 0) return r + "s"
      const resto = String(r)
      return m + " min " + (resto.length < 2 ? "0" + resto : resto) + "s"
    },
    duracionDeArchivo(file) {
      return new Promise((resolve) => {
        if (!file) return resolve(0)
        const url = URL.createObjectURL(file)
        const video = document.createElement("video")
        video.preload = "metadata"
        video.onloadedmetadata = () => {
          const d = Math.round(video.duration)
          URL.revokeObjectURL(url)
          resolve(Number.isFinite(d) && d > 0 ? d : 0)
        }
        video.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(0)
        }
        video.src = url
      })
    },
    videoPermitido(file) {
      if (!file) return false
      if (file.size > MAX_VIDEO) {
        this.error = "El video no puede pasar de 1 GB"
        return false
      }
      return true
    },
    urlPortada(l) {
      const base = (this.$http.defaults && this.$http.defaults.baseURL) || "/api-rh/"
      const token = window.localStorage.getItem("accessToken") || window.localStorage.getItem("tlaKey") || ""
      const root = String(base).charAt(String(base).length - 1) === "/" ? String(base) : String(base) + "/"
      return root + "curso.virtual.portada/" + l.id + "?token=" + encodeURIComponent(token)
    },
    tienePortada(l) {
      return !!(this.portadasLocal[l.id] || (l && l.portada))
    },
    cajaDe(frame, imgW, imgH, x, y, zoom) {
      if (!frame || !imgW || !imgH) return null
      const frameW = frame.clientWidth
      const frameH = frame.clientHeight
      if (!frameW || !frameH) return null
      const z = Math.max(Number(zoom) || 100, 100) / 100
      const frameAspect = frameW / frameH
      const imgAspect = imgW / imgH
      const widthPct = (imgAspect < frameAspect ? 100 : (imgAspect / frameAspect) * 100) * z
      const heightPct = (imgAspect > frameAspect ? 100 : (frameAspect / imgAspect) * 100) * z
      const px = x == null ? 50 : Number(x)
      const py = y == null ? 50 : Number(y)
      return {
        width: widthPct + "%",
        height: heightPct + "%",
        left: -(widthPct - 100) * (px / 100) + "%",
        top: -(heightPct - 100) * (py / 100) + "%",
        objectFit: "fill",
        transform: "none",
        slackX: ((widthPct - 100) / 100) * frameW,
        slackY: ((heightPct - 100) / 100) * frameH,
        leftPx: -((widthPct - 100) / 100) * frameW * (px / 100),
        topPx: -((heightPct - 100) / 100) * frameH * (py / 100),
      }
    },
    estiloPreview(l) {
      const ref = this.$refs["vista-" + l.id]
      const frame = Array.isArray(ref) ? ref[0] : ref
      const caja = this.cajaDe(frame, l._nw, l._nh, l.portada_x, l.portada_y, l.portada_zoom)
      if (!caja) return { width: "100%", height: "100%", left: "0", top: "0", objectFit: "cover" }
      return { width: caja.width, height: caja.height, left: caja.left, top: caja.top, objectFit: "fill", transform: "none" }
    },
    estiloAjuste() {
      if (!this.ajuste) return {}
      const caja = this.cajaDe(this.$refs.marcoAjuste, this.ajuste.nw, this.ajuste.nh, this.ajuste.x, this.ajuste.y, this.ajuste.zoom)
      if (!caja) return { width: "100%", height: "100%", left: "0", top: "0", objectFit: "cover" }
      return { width: caja.width, height: caja.height, left: caja.left, top: caja.top, objectFit: "fill", transform: "none" }
    },
    medirPreview(l, e) {
      const img = e.target
      if (!img.naturalWidth) return
      this.$set(l, "_nw", img.naturalWidth)
      this.$set(l, "_nh", img.naturalHeight)
    },
    medirAjuste(e) {
      const img = e.target
      if (!this.ajuste || !img.naturalWidth) return
      this.ajuste.nw = img.naturalWidth
      this.ajuste.nh = img.naturalHeight
      this.$forceUpdate()
    },
    abrirPortada(id) {
      const ref = this.$refs["portada-" + id]
      const input = Array.isArray(ref) ? ref[0] : ref
      if (input) input.click()
    },
    elegirPortada(e, l) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.error = "La portada no puede pasar de 5 MB"
        e.target.value = ""
        return
      }
      if (this.portadasLocal[l.id]) URL.revokeObjectURL(this.portadasLocal[l.id])
      this.$set(this.portadas, l.id, file)
      this.$set(this.portadasLocal, l.id, URL.createObjectURL(file))
      this.abrirAjuste(l)
    },
    abrirAjuste(l) {
      const src = this.portadasLocal[l.id] || (l.portada ? this.urlPortada(l) : "")
      if (!src) return
      this.errorAjuste = ""
      this.ajuste = {
        leccion: l,
        src: src,
        nw: l._nw || 0,
        nh: l._nh || 0,
        x: l.portada_x == null ? 50 : Number(l.portada_x),
        y: l.portada_y == null ? 50 : Number(l.portada_y),
        zoom: l.portada_zoom == null ? 100 : Number(l.portada_zoom),
      }
      this.modalAjuste = true
    },
    empezarArrastre(e) {
      if (!this.ajuste || e.button !== 0) return
      e.preventDefault()
      const caja = this.cajaDe(this.$refs.marcoAjuste, this.ajuste.nw, this.ajuste.nh, this.ajuste.x, this.ajuste.y, this.ajuste.zoom)
      this.arrastre = {
        x: e.clientX,
        y: e.clientY,
        zoom: this.ajuste.zoom,
        left: caja ? caja.leftPx : 0,
        top: caja ? caja.topPx : 0,
      }
      window.addEventListener("mousemove", this.moverArrastre)
      window.addEventListener("mouseup", this.soltarArrastre)
    },
    moverArrastre(e) {
      if (!this.arrastre || !this.ajuste) return
      const marco = this.$refs.marcoAjuste
      if (!marco || !this.ajuste.nw || !this.ajuste.nh) return
      let zoom = this.arrastre.zoom
      let left = this.arrastre.left + (e.clientX - this.arrastre.x)
      let top = this.arrastre.top + (e.clientY - this.arrastre.y)
      const holgura = () => {
        const caja = this.cajaDe(marco, this.ajuste.nw, this.ajuste.nh, 0, 0, zoom)
        return caja || { slackX: 0, slackY: 0 }
      }
      let m = holgura()
      let guard = 0
      while (guard < 30 && zoom < 250 && (left < -m.slackX - 8 || left > 8 || top < -m.slackY - 8 || top > 8)) {
        const prevX = m.slackX
        const prevY = m.slackY
        zoom += 5
        m = holgura()
        left -= (m.slackX - prevX) / 2
        top -= (m.slackY - prevY) / 2
        guard += 1
      }
      left = Math.max(-m.slackX, Math.min(0, left))
      top = Math.max(-m.slackY, Math.min(0, top))
      this.ajuste.zoom = zoom
      this.ajuste.x = m.slackX > 1 ? Math.round((-left / m.slackX) * 100) : 50
      this.ajuste.y = m.slackY > 1 ? Math.round((-top / m.slackY) * 100) : 50
    },
    soltarArrastre() {
      this.arrastre = null
      window.removeEventListener("mousemove", this.moverArrastre)
      window.removeEventListener("mouseup", this.soltarArrastre)
    },
    cerrarAjuste() {
      if (this.guardandoAjuste) return
      this.modalAjuste = false
      this.ajuste = null
      this.arrastre = null
    },
    guardarAjuste() {
      const ajuste = this.ajuste
      if (!ajuste || !ajuste.leccion || this.guardandoAjuste) return
      const l = ajuste.leccion
      const file = this.portadas[l.id]
      this.guardandoAjuste = true
      this.errorAjuste = ""
      this.error = ""
      l.portada_x = ajuste.x
      l.portada_y = ajuste.y
      l.portada_zoom = ajuste.zoom
      const subir = file
        ? this.$http.post("curso.virtual.portada/" + l.id, this.formPortada(file, ajuste))
        : Promise.resolve({ data: { portada: l.portada } })
      subir.then((r) => {
        const data = r.data || r.body || {}
        if (data.portada) l.portada = data.portada
        return this.$http.put("curso.virtual.lecciones/" + l.id, {
          titulo: l.titulo,
          descripcion: l.descripcion || "",
          orden: l.orden,
          duracion_segundos: l.duracion_segundos,
          codigo: l.codigo || String(l.id),
          video_provider: l.video_provider,
          video_id: l.video_id,
          portada_x: ajuste.x,
          portada_y: ajuste.y,
          portada_zoom: ajuste.zoom,
        })
      }).then(() => {
        this.$delete(this.portadas, l.id)
        this.ok = "Portada guardada. Ya se ve en la app al abrir el nivel."
        this.modalAjuste = false
        this.ajuste = null
        this.arrastre = null
      }).catch((e) => {
        this.errorAjuste = mensaje(e, "No se pudo guardar la portada")
      }).finally(() => {
        this.guardandoAjuste = false
      })
    },
    formPortada(file, ajuste) {
      const form = new FormData()
      form.append("portada", file)
      form.append("portada_x", String(ajuste.x))
      form.append("portada_y", String(ajuste.y))
      form.append("portada_zoom", String(ajuste.zoom))
      return form
    },
    urlVideo(l) {
      const base = (this.$http.defaults && this.$http.defaults.baseURL) || "/api-rh/"
      const token = window.localStorage.getItem("accessToken") || window.localStorage.getItem("tlaKey") || ""
      const root = String(base).charAt(String(base).length - 1) === "/" ? String(base) : String(base) + "/"
      return root + "curso.virtual.video/" + l.id + "?token=" + encodeURIComponent(token) + "#t=0.1"
    },
    toggleLeccion(l) {
      const abrir = this.abierta !== l.id
      this.abierta = abrir ? l.id : null
      if (abrir) this.acomodarBloque("leccion-" + l.id)
    },
    actualizarVista() {
      this.actualizando = true
      this.error = ""
      this.cargar().then(() => {
        this.ok = "Vista actualizada (orden de lecciones)"
      }).finally(() => {
        this.actualizando = false
      })
    },
    abrirFile(id) {
      const el = this.$refs["file-" + id]
      const input = Array.isArray(el) ? el[0] : el
      if (input) input.click()
    },
    abrirFileNueva() {
      if (this.$refs.fileNueva) this.$refs.fileNueva.click()
    },
    elegirArchivoNueva(e) {
      const file = (e.target.files && e.target.files[0]) || null
      if (file && !this.videoPermitido(file)) {
        e.target.value = ""
        this.archivoNueva = null
        return
      }
      this.archivoNueva = file
    },
    elegirArchivo(e, l) {
      const file = (e.target.files && e.target.files[0]) || null
      if (file && !this.videoPermitido(file)) {
        e.target.value = ""
        this.$set(this.archivos, l.id, null)
        return
      }
      this.$set(this.archivos, l.id, file || null)
    },
    cargar() {
      this.error = ""
      return this.$http.get("curso.virtual.lecciones/" + this.nivel.id, {
        params: { _: Date.now() },
      }).then((r) => {
        const data = r.data || r.body || {}
        this.lecciones = data.lecciones || []
        let maxOrden = 0
        this.lecciones.forEach((l) => {
          maxOrden = Math.max(maxOrden, Number(l.orden) || 0)
        })
        this.nueva.orden = maxOrden + 1
        if (this.abierta && !this.lecciones.some((x) => x.id === this.abierta)) this.abierta = null
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo cargar")
      })
    },
    subir(l) {
      const file = this.archivos[l.id]
      if (!file) {
        this.error = "Elige un archivo de video primero"
        return
      }
      this.error = ""
      this.ok = ""
      this.subiendo = l.id
      this.$set(this.progreso, l.id, 0)
      this.duracionDeArchivo(file).then((duracion) => {
        const form = new FormData()
        form.append("video", file)
        if (duracion > 0) form.append("duracion_segundos", String(duracion))
        return this.$http.post("curso.virtual.video/" + l.id, form, {
          timeout: 15 * 60 * 1000,
          onUploadProgress: (ev) => {
            if (!ev.total) return
            this.$set(this.progreso, l.id, Math.round((ev.loaded / ev.total) * 100))
          },
        }).then((r) => {
          const data = r.data || r.body || {}
          l.video_provider = data.video_provider
          l.video_id = data.video_id
          if (data.duracion_segundos) l.duracion_segundos = data.duracion_segundos
          else if (duracion > 0) l.duracion_segundos = duracion
          this.ok = "Video subido"
          this.$set(this.archivos, l.id, null)
          this.$set(this.progreso, l.id, 100)
          return this.cargar()
        })
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo subir el video")
      }).finally(() => {
        this.subiendo = null
      })
    },
    pedirGuardarLeccion(l) {
      this.confirmDialog = {
        tipo: "leccion",
        leccion: l,
        eyebrow: "Guardar lección",
        titulo: "¿Guardar “" + (l.titulo || "lección") + "”?",
        cuerpo: "Se guardarán el título, la descripción y el orden de esta clase.",
        aceptar: "Guardar",
        peligro: false,
        cargando: false,
      }
    },
    pedirEliminarLeccion(l) {
      this.confirmDialog = {
        tipo: "eliminar_leccion",
        leccion: l,
        eyebrow: "Eliminar lección",
        titulo: "¿Eliminar “" + (l.titulo || "lección") + "”?",
        cuerpo: "Se borrarán el video, notas y progreso asociados. Esta acción no se puede deshacer.",
        aceptar: "Eliminar",
        peligro: true,
        cargando: false,
      }
    },
    cerrarConfirm() {
      if (this.confirmDialog && this.confirmDialog.cargando) return
      this.confirmDialog = null
    },
    aceptarConfirm() {
      if (!this.confirmDialog) return
      if (this.confirmDialog.tipo === "leccion") this.guardarLeccion(this.confirmDialog.leccion)
      if (this.confirmDialog.tipo === "eliminar_leccion") this.eliminarLeccion(this.confirmDialog.leccion)
    },
    guardarLeccion(l) {
      this.ok = ""
      this.error = ""
      if (this.confirmDialog) this.confirmDialog.cargando = true
      this.$http.put("curso.virtual.lecciones/" + l.id, {
        titulo: l.titulo,
        descripcion: l.descripcion || "",
        orden: l.orden,
        duracion_segundos: l.duracion_segundos,
        codigo: l.codigo || String(l.id),
        video_provider: l.video_provider,
        video_id: l.video_id,
        portada_x: l.portada_x == null ? 50 : l.portada_x,
        portada_y: l.portada_y == null ? 50 : l.portada_y,
        portada_zoom: l.portada_zoom == null ? 100 : l.portada_zoom,
      }).then((r) => {
        const data = r.data || r.body || {}
        if (data.video_id) {
          l.video_id = data.video_id
          l.video_provider = "local"
        }
        this.ok = "Lección guardada. Usa “Actualizar vista” para reordenar la lista."
        this.confirmDialog = null
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo guardar la lección")
        if (this.confirmDialog) this.confirmDialog.cargando = false
      })
    },
    eliminarLeccion(l) {
      this.ok = ""
      this.error = ""
      if (this.confirmDialog) this.confirmDialog.cargando = true
      this.$http.delete("curso.virtual.lecciones/" + l.id).then((r) => {
        const data = r.data || r.body || {}
        this.ok = data.message || "Lección eliminada"
        if (this.previews[l.id]) {
          URL.revokeObjectURL(this.previews[l.id])
          this.$delete(this.previews, l.id)
        }
        this.abierta = null
        this.confirmDialog = null
        this.cargar()
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo eliminar la lección")
        if (this.confirmDialog) this.confirmDialog.cargando = false
      })
    },
    crearLeccion() {
      const titulo = String(this.nueva.titulo || "").trim()
      const descripcion = String(this.nueva.descripcion || "").trim()
      const faltantes = []
      if (!titulo) faltantes.push("el título")
      if (!descripcion) faltantes.push("la descripción")
      this.faltantesNueva = faltantes
      if (faltantes.length) return
      this.error = ""
      this.ok = ""
      this.creando = true
      this.progresoNueva = 0
      this.$http.post("curso.virtual.lecciones", {
        id_nivel: this.nivel.id,
        titulo: titulo,
        descripcion: descripcion,
        orden: Number(this.nueva.orden) || 0,
      }).then((r) => {
        const data = r.data || r.body || {}
        const id = data.id
        const orden = Number(this.nueva.orden) || 0
        if (id) {
          this.lecciones = this.lecciones.concat([{
            id: id,
            titulo: titulo,
            descripcion: descripcion,
            orden: orden,
            duracion_segundos: 0,
            video_id: null,
            video_provider: null,
          }])
        }
        const video = this.archivoNueva
        const siguiente = orden + 1
        this.nueva = { titulo: "", descripcion: "", orden: siguiente }
        this.archivoNueva = null
        this.faltantesNueva = []
        if (this.$refs.fileNueva) this.$refs.fileNueva.value = ""
        if (!(video && id)) return this.cargar()
        return this.duracionDeArchivo(video).then((duracion) => {
          const form = new FormData()
          form.append("video", video)
          if (duracion > 0) form.append("duracion_segundos", String(duracion))
          return this.$http.post("curso.virtual.video/" + id, form, {
            timeout: 15 * 60 * 1000,
            onUploadProgress: (ev) => {
              if (!ev.total) return
              this.progresoNueva = Math.round((ev.loaded / ev.total) * 100)
            },
          })
        }).then(() => this.cargar())
      }).then(() => {
        this.ok = "Lección agregada"
        this.progresoNueva = 100
      }).catch((e) => {
        this.error = mensaje(e, "No se pudo crear la lección")
      }).finally(() => {
        this.creando = false
        this.progresoNueva = 0
      })
    },
  },
}
</script>

<style scoped>
.cfg { max-width: 1100px; }
.muted { border: 0; background: transparent; color: #64748b; font-weight: 600; font-size: 14px; cursor: pointer; padding: 0; }
.head { margin: 10px 0 24px; }
.eyebrow { margin: 0; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; color: #64748b; text-transform: uppercase; }
.head h1 { font-weight: 800; letter-spacing: -0.03em; margin: 4px 0 6px; font-size: 32px; color: #0f172a; }
.lead { color: #64748b; margin: 0; }
.soft-card, .lesson-card { background: #fff; border-radius: 20px; box-shadow: 0 14px 40px rgba(15, 23, 42, 0.06); border: 1px solid #eef2f7; }
.nivel-box { padding: 22px 24px 24px; margin-top: 8px; }
.box-title, .section-h { margin: 0; font-size: 18px; font-weight: 800; color: #0f172a; }
.box-title { margin-bottom: 16px; }
.nivel-info { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 24px; margin-bottom: 12px; }
.info-item.full { grid-column: 1 / -1; }
.info-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
.info-value { margin: 0; font-size: 16px; font-weight: 700; color: #0f172a; }
.nivel-hint { margin: 4px 0 0; font-size: 13px; color: #64748b; }
.panel-secciones { display: flex; flex-wrap: wrap; gap: 8px; margin: 18px 0 8px; }
.panel-secciones button { border: 1px solid #e2e8f0; background: #fff; color: #334155; border-radius: 999px; padding: 8px 14px; font-weight: 700; font-family: inherit; cursor: pointer; }
.panel-secciones button.on { background: #0e7c78; color: #fff; border-color: #0e7c78; }
.vacio-seccion { padding: 22px 24px 24px; margin-top: 18px; }
.frentes { margin-top: 0; }
.campo-label { display: block; margin: 16px 0 6px; font-size: 13px; font-weight: 800; color: #334155; }
.despliegue { margin-top: 12px; }
.mensajes-box { margin-top: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 16px; background: #f8fafc; }
.mensaje-item { margin-bottom: 12px; }
.mensaje-item .campo-area { margin-top: 8px; }
.campo-area, .campo-texto, .campo-num { width: 100%; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 12px; font: inherit; color: #0f172a; background: #fff; box-sizing: border-box; }
.campo-num { max-width: 120px; }
.fila-enunciado { display: flex; gap: 10px; align-items: center; margin-bottom: 14px; }
.fila-enunciado .campo-texto { flex: 1; }
.enunciado-card .lesson-summary { align-items: center; }
.palabras { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0; }
.palabra-chip { border: 1px solid #e2e8f0; background: #f8fafc; color: #0f172a; border-radius: 999px; padding: 6px 12px; font-weight: 700; cursor: pointer; font-family: inherit; }
.palabra-chip.on { background: #0e7c78; color: #fff; border-color: #0e7c78; }
.enunciado-vista { margin: 8px 0; font-weight: 800; color: #0f172a; }
.tres-palabras { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 28px 0 12px; }
.btn-quitar { display: block; margin-top: 3px; }
.btn-agregar-audio { margin-top: 3px; }
.audio-prev { display: block; width: 100%; margin: 12px 0; }
.voc-agregar { margin-top: 3px; }
.voc-editor { display: flex; gap: 22px; align-items: center; margin-bottom: 14px; flex-wrap: wrap; }
.voc-circulo { width: 180px; height: 180px; border-radius: 50%; overflow: hidden; position: relative; background: #e0f2fe; border: 6px solid #7dd3fc; box-shadow: 0 0 0 10px #f0f9ff; cursor: grab; flex: 0 0 auto; }
.voc-circulo img { position: absolute; max-width: none; }
.voc-vacio { position: absolute; inset: 0; display: grid; place-items: center; color: #64748b; font-weight: 700; font-size: 13px; }
.voc-lado { flex: 1; min-width: 180px; }
.grabando-box { display: flex; align-items: center; gap: 14px; margin: 0 0 12px; padding: 14px 16px; border-radius: 16px; background: #fff1f2; border: 1px solid #fecdd3; }
.grabando-box strong { display: block; color: #be123c; font-size: 15px; }
.grabando-box p { margin: 2px 0 0; font-variant-numeric: tabular-nums; font-weight: 800; color: #0f172a; font-size: 20px; }
.grabando-punto { width: 14px; height: 14px; border-radius: 50%; background: #e11d48; flex: 0 0 auto; animation: grabando-pulso 1s ease-in-out infinite; }
.grabando-stop { margin-left: auto; background: #e11d48; }
@keyframes grabando-pulso { 50% { opacity: 0.35; } }
.lesson-card { margin-bottom: 12px; overflow: hidden; scroll-margin-top: 88px; }
.lesson-card.add { border-style: dashed; padding: 24px 28px 28px; }
.lesson-card.add h3 { margin: 0 0 16px; font-size: 18px; font-weight: 800; }
.lesson-summary { width: 100%; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding: 18px 20px; border: 0; background: transparent; text-align: left; cursor: pointer; font-family: inherit; }
.lesson-card.open .lesson-summary { border-bottom: 1px solid #eef2f7; background: #f8fafc; }
.summary-main { min-width: 0; flex: 1; }
.summary-main h3 { margin: 0 0 6px; font-size: 17px; font-weight: 800; color: #0f172a; }
.summary-desc { margin: 0; color: #64748b; font-size: 13px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.summary-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.chevron { font-size: 16px; color: #64748b; font-weight: 700; }
.badge { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; padding: 6px 12px; border-radius: 999px; }
.badge.ok { background: #ecfdf5; color: #047857; }
.badge.off { background: #f1f5f9; color: #64748b; }
.lesson-body { padding: 20px 22px 24px; }
.grid-2 { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 28px; align-items: start; }
.row-nueva { display: grid; grid-template-columns: 1.6fr 0.6fr; gap: 12px; margin-bottom: 14px; }
.nueva-video, .video-panel { background: #f8fafc; border-radius: 18px; padding: 20px; }
.nueva-video { margin-bottom: 14px; padding: 14px; border-radius: 16px; }
.panel-label { font-size: 12px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #94a3b8; margin: 0 0 10px; }
.hidden-file { display: none; }
.file-ok { color: #0e7c78; font-weight: 600; font-size: 13px; margin: 0 0 12px; }
.file-miss { color: #94a3b8; font-size: 13px; margin: 0 0 12px; }
.picked { font-size: 13px; font-weight: 600; color: #334155; margin: 0 0 10px; }
.actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.faltantes { margin: 0 0 12px; color: #b91c1c; font-weight: 700; font-size: 14px; }
.duracion-auto { margin: 0 0 14px; font-size: 14px; color: #334155; }
.duracion-auto .hint { color: #94a3b8; font-size: 12px; }
.btn { height: 42px; padding: 0 18px; border: 0; border-radius: 999px; background: #0e7c78; color: #fff; font-weight: 700; cursor: pointer; font-family: inherit; }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn.ghost { background: #f1f5f9; color: #0f172a; }
.btn.danger { background: #b91c1c; }
.btn.refresh { background: #2563eb; color: #fff; }
.btn-del-lesson { margin-left: 10px; height: 42px; padding: 0 18px; border: 0; border-radius: 999px; background: transparent; color: #b91c1c; font-weight: 700; cursor: pointer; font-family: inherit; }
.upload-pct { margin: 6px 0 0; font-size: 12px; font-weight: 700; color: #0e7c78; }
.preview { display: block; width: 100%; max-height: 280px; margin-top: 14px; border-radius: 14px; background: #020617; }
.portada-box { margin-top: 18px; padding-top: 8px; }
.preview-label { margin: 16px 0 8px; font-size: 12px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #64748b; }
.app-preview { position: relative; overflow: hidden; width: 400px; max-width: 100%; border: 1px solid #e2e8f0; border-radius: 18px; padding: 14px; background: #fff; display: flex; flex-direction: column; min-height: 148px; box-sizing: border-box; }
.app-preview-foto { position: absolute; top: 0; left: 0; max-width: none; pointer-events: none; }
.app-preview .preview-kicker,
.app-preview strong,
.app-preview p,
.app-preview .preview-cta { position: relative; z-index: 1; }
.app-preview .preview-kicker { font-size: 12px; font-weight: 700; color: #94a3b8; }
.app-preview strong { font-size: 16px; color: #0f172a; margin-top: 0; }
.app-preview p { margin: 6px 0 0; color: #64748b; font-size: 13px; line-height: 1.4; }
.app-preview .preview-cta { align-self: flex-end; margin-top: 10px; background: #0ea5e9; color: #fff; border-radius: 999px; padding: 8px 14px; font-weight: 800; font-size: 14px; }
.app-preview.con-foto::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(15,23,42,.12), rgba(15,23,42,.5)); }
.app-preview.con-foto .preview-kicker,
.app-preview.con-foto strong,
.app-preview.con-foto p { color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,.35); }
.ajuste-card { padding: 22px 22px 18px; }
.ajuste-card h3 { margin: 0 0 6px; color: #0f172a; }
.ajuste-card p { margin: 0 0 12px; color: #64748b; }
.marco { cursor: grab; box-shadow: 0 0 0 3px rgba(125,211,252,.35); }
.zoom-label { display: block; margin: 14px 0 6px; font-weight: 700; color: #334155; }
.zoom-range { width: 100%; margin-bottom: 16px; }
.confirm-overlay { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(15, 23, 42, 0.42); }
.confirm-card { width: min(420px, 100%); padding: 28px 24px 22px; }
.confirm-eyebrow { margin: 0 0 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #64748b; }
.confirm-card h3 { margin: 0 0 10px; font-size: 22px; color: #0f172a; }
.confirm-body { margin: 0 0 22px; color: #64748b; }
.confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
@media (max-width: 900px) {
  .nivel-info, .grid-2, .row-nueva { grid-template-columns: 1fr; }
}
</style>
